import { Navigate, Outlet } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useAuth, useConfig } from '@chainlit/react-client';

import LeftSidebar from '@/components/LeftSidebar';
import UserSidebar from '@/components/UserSidebar/UserSidebar';
import DynamicLayout from '@/components/DynamicLayout';
import { Header } from '@/components/header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import OperationsDialog from '@/components/operations/OperationsDialog';
import OperationModal from '@/components/operations/OperationModal';
import ROEFormDialog from '@/components/roe/ROEFormDialog';
import ROEFormListener from '@/components/roe/ROEFormListener';
import ReportFormDialog from '@/components/report/ReportFormDialog';
import ReportFormListener from '@/components/report/ReportFormListener';
import DownloadListener from '@/components/report/DownloadListener';
import RewindConfirmDialog from '@/components/rewind/RewindConfirmDialog';
import RewindConfirmListener from '@/components/rewind/RewindConfirmListener';
import EditRewindListener from '@/components/rewind/EditRewindListener';
import SnapshotHistoryListener from '@/components/rewind/SnapshotHistoryListener';
import TargetSelectDialog from '@/components/target-select/TargetSelectDialog';
import TargetSelectListener from '@/components/target-select/TargetSelectListener';
import AuditLogDialog from '@/components/admin/AuditLogDialog';
import UserDetailDialog from '@/components/admin/UserDetailDialog';
import AccountExpiredListener from '@/components/AccountExpiredListener';
import AccountExpiredModal from '@/components/AccountExpiredModal';
import ComposerDraftReset from '@/components/chat/ComposerDraftReset';

import { userEnvState } from 'state/user';

type Props = {
  children?: JSX.Element;
};

/**
 * Shared page layout with header, sidebar, and dynamic content area.
 * When used as a layout route element (no children), renders Outlet.
 * When used directly with children (e.g. Element page), renders children.
 */
const Page = ({ children }: Props) => {
  const { config } = useConfig();
  const { data } = useAuth();
  const userEnv = useRecoilValue(userEnvState);

  if (config?.userEnv) {
    for (const key of config.userEnv || []) {
      if (!userEnv[key]) return <Navigate to="/env" />;
    }
  }

  const content = children || <Outlet />;

  const mainContent = (
    <div className="flex flex-col h-full w-full">
      <Header />
      <div className="flex flex-row flex-grow overflow-auto">
        <DynamicLayout>{content}</DynamicLayout>
      </div>
      <UserSidebar />
    </div>
  );

  const historyEnabled = config?.dataPersistence && data?.requireLogin;
  const sidebarHidden = config?.ui?.default_sidebar_state === 'hidden';

  return (
    <SidebarProvider
      defaultOpen={config?.ui.default_sidebar_state !== 'closed'}
    >
      {historyEnabled && !sidebarHidden ? (
        <>
          <LeftSidebar collapsible="icon" />
          <SidebarInset className="max-h-svh min-w-0">
            {mainContent}
          </SidebarInset>
        </>
      ) : (
        <div className="h-screen w-screen flex">{mainContent}</div>
      )}
      <OperationsDialog />
      <OperationModal />
      <ROEFormDialog />
      <ROEFormListener />
      <ReportFormDialog />
      <ReportFormListener />
      <DownloadListener />
      <RewindConfirmDialog />
      <RewindConfirmListener />
      <EditRewindListener />
      <SnapshotHistoryListener />
      <TargetSelectDialog />
      <TargetSelectListener />
      <UserDetailDialog />
      <AuditLogDialog />
      <AccountExpiredListener />
      <AccountExpiredModal />
      <ComposerDraftReset />
    </SidebarProvider>
  );
};

export default Page;
